import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "modal",
    "recordButton",
    "recordLabel",
    "resetButton",
    "submitButton",
    "spinner",
    "status",
    "result",
    "player",
    "meter",
    "modeButton",
    "formAction",
    "signedId",
    "cached",
    "resultText",
    "notesField",
  ];

  connect() {
    this.mode = "summarize";
    this.mediaRecorder = null;
    this.chunks = [];
    this.stream = null;
  }

  openModal() {
    this.modalTarget.style.display = "block";
  }

  close() {
    this.stopRecording({ keepAudio: true });
    this.modalTarget.style.display = "none";
  }

  async startMic() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Prefer Opus at a reduced bitrate to shrink upload size and speed processing
      const preferredTypes = [
        "audio/webm;codecs=opus",
        "audio/ogg;codecs=opus",
        "audio/webm",
      ];
      let mimeType = "";
      for (const t of preferredTypes) {
        if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) {
          mimeType = t;
          break;
        }
      }
      const options = mimeType
        ? { mimeType, audioBitsPerSecond: 64000 }
        : { audioBitsPerSecond: 64000 };

      this.mediaRecorder = new MediaRecorder(this.stream, options);
      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this.chunks.push(e.data);
      };
      this.mediaRecorder.onstop = () => this.onStop();
      this.statusTarget.textContent = "Microphone ready. Click Start Recording.";
    } catch (e) {
      console.error(e);
      this.statusTarget.textContent = "Microphone access denied.";
    }
  }

  async toggleRecording() {
    // Lazily initialize microphone on first click
    if (!this.mediaRecorder) {
      try {
        await this.startMic();
      } catch (e) {
        this.statusTarget.textContent = "Microphone access denied.";
        return;
      }
    }

    if (this.mediaRecorder.state === "inactive") {
      this.chunks = [];
      this.mediaRecorder.start();
      if (this.hasRecordLabelTarget) {
        this.recordLabelTarget.textContent = "Stop Recording";
      } else {
        this.recordButtonTarget.textContent = "Stop Recording";
      }
      this.resetButtonTarget.disabled = true;
      this.submitButtonTarget.disabled = true;
      this.statusTarget.textContent = "Recording...";
    } else {
      this.mediaRecorder.stop();
      // Close the microphone immediately on stop
      if (this.stream) {
        try {
          this.stream.getTracks().forEach((t) => t.stop());
        } catch (e) {
          console.warn("Failed to stop mic tracks", e);
        }
        this.stream = null;
      }
      if (this.hasRecordLabelTarget) {
        this.recordLabelTarget.textContent = "Start Recording";
      } else {
        this.recordButtonTarget.textContent = "Start Recording";
      }
      this.statusTarget.textContent = "Recording stopped.";
    }
  }

  onStop() {
    // Use the recorder's selected MIME type when available
    const recordedType = this.mediaRecorder && this.mediaRecorder.mimeType ? this.mediaRecorder.mimeType : "audio/webm";
    const blob = new Blob(this.chunks, { type: recordedType });
    this.recordedBlob = blob;
    const url = URL.createObjectURL(blob);
    this.playerTarget.src = url;
    this.playerTarget.style.display = "block";
    this.resetButtonTarget.disabled = false;
    this.submitButtonTarget.disabled = false;
    // Ensure recorder instance is cleared after finishing
    try { this.mediaRecorder = null; } catch (_) {}
  }

  reset() {
    this.chunks = [];
    this.recordedBlob = null;
    this.playerTarget.removeAttribute("src");
    this.playerTarget.style.display = "none";
    this.resetButtonTarget.disabled = true;
    this.submitButtonTarget.disabled = true;
    this.statusTarget.textContent = "Ready to record.";
  }

  stopRecording({ keepAudio = false } = {}) {
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }
    if (!keepAudio && this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
    }
  }

  selectMode(event) {
    const mode = event.currentTarget.dataset.mode;
    this.mode = mode;
    this.modeButtonTargets.forEach((btn) => {
      btn.setAttribute("aria-selected", btn.dataset.mode === mode ? "true" : "false");
    });
  }

  async submit() {
    if (!this.recordedBlob) return;
    this.statusTarget.textContent = "Uploading and processing...";
    this.submitButtonTarget.disabled = true;
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.style.display = "inline-flex";
      this.spinnerTarget.setAttribute("aria-hidden", "false");
    }

    const formData = new FormData();
    formData.append("audio", this.recordedBlob, "recording.webm");
    formData.append("mode", this.mode);

    try {
      const resp = await fetch(this.formActionTarget.value, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken() },
        body: formData,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Processing failed");

      if (this.hasCachedTarget) this.cachedTarget.value = data.cached;
      if (data.text && this.hasNotesFieldTarget) {
        const current = this.notesFieldTarget.value || "";
        this.notesFieldTarget.value = data.text;
      }
      this.resultTarget.style.display = "block";
      this.resultTarget.textContent = "Voice note processed and inserted into notes.";
      this.statusTarget.textContent = "";
      if (this.hasSpinnerTarget) {
        this.spinnerTarget.style.display = "none";
        this.spinnerTarget.setAttribute("aria-hidden", "true");
      }
    } catch (e) {
      console.error(e);
      this.statusTarget.textContent = e.message;
      this.submitButtonTarget.disabled = false;
      if (this.hasSpinnerTarget) {
        this.spinnerTarget.style.display = "none";
        this.spinnerTarget.setAttribute("aria-hidden", "true");
      }
    }
  }

  csrfToken() {
    const el = document.querySelector('meta[name="csrf-token"]');
    return el ? el.getAttribute("content") : "";
  }
}


